import { ReactElement, useState, useCallback, useMemo } from 'react';
import { Typography, Stack, Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { defaultTo } from 'lodash';
import moment from 'moment';

import { LoadingFallback } from '~components/index';
import { useLazyRequest, useInfiniteScroll } from '~hooks/index';
import { searchRoutes } from '~services/route';
import { RouteFilter, SearchRoute, RouteStats } from '~services/route/types';
import {
  PaginationRequestParams,
  DataPaginateResponse,
  ErrorResponse,
} from '~globals/types';
import { RouteStateTypes } from '~globals/types/enums';
import { formatterDate } from '~utils/formatter';
import { toAbsoluteUrl } from '~utils/assetsHelpers';

import { AddOrdersToRouteMenuProps } from '../../types';
import AddOrdersToRouteMenuRouteCard from '../../RouteCard';
import {
  HEIGHT_ROUTE_CARD,
  AddOrdersToRouteMenuContent,
  AddOrdersToRouteMenuContentList,
} from '../../styles';

const defaultSizePerPage = 10;

const AddOrdersToRouteMenuPending = ({
  onClickCard,
}: Pick<AddOrdersToRouteMenuProps, 'onClickCard'>): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [pendingRoutes, setPendingRoutes] = useState<SearchRoute<true>[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [page, setPage] = useState(1);

  const [
    ,
    loadingFetchPendingRoutes,
    errorFetchPendingRoutes,
    executeFetchPendingRoutes,
  ] = useLazyRequest<
    PaginationRequestParams<RouteFilter<true>>,
    DataPaginateResponse<SearchRoute<true>[], RouteStats>,
    ErrorResponse
  >({
    request: searchRoutes,
    withPostSuccess: (response) => {
      const {
        data: { pagination, results },
      } = response.data as DataPaginateResponse<
        SearchRoute<true>[],
        RouteStats
      >;

      const newPendingRoutes = [...pendingRoutes, ...results];
      setPendingRoutes(newPendingRoutes);

      const totalSize = defaultTo(pagination?.count, results.length);
      setHasNextPage(newPendingRoutes.length < totalSize);

      setPage((prevPage) => prevPage + 1);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const onLoadMore = useCallback(() => {
    const fromDate = new Date(
      moment().subtract(3, 'days').startOf('day').format(),
    );

    const toDate = new Date(moment().add(3, 'days').endOf('day').format());

    executeFetchPendingRoutes({
      pagination: {
        page,
        sizePerPage: defaultSizePerPage,
      },
      filters: {
        dateFrom: formatterDate(fromDate, {
          format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
        }),
        dateTo: formatterDate(toDate, {
          format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
        }),
        routeCode: null,
        driversId: null,
        vehiclesId: null,
        routeStateTypesId: [
          RouteStateTypes.New,
          RouteStateTypes.Assigned,
          RouteStateTypes.Approve,
        ],
        includeApiTransaction: true,
      },
    });
  }, [executeFetchPendingRoutes, page]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: loadingFetchPendingRoutes,
    hasNextPage,
    onLoadMore,
    disabled: !!errorFetchPendingRoutes,
    rootMargin: `0px 0px ${HEIGHT_ROUTE_CARD}px 0px`,
  });

  const showLoading = useMemo(
    () => loadingFetchPendingRoutes || hasNextPage,
    [loadingFetchPendingRoutes, hasNextPage],
  );

  const notActiveRoutes = useMemo(
    () => !loadingFetchPendingRoutes && !hasNextPage && !pendingRoutes.length,
    [loadingFetchPendingRoutes, hasNextPage, pendingRoutes],
  );

  return (
    <AddOrdersToRouteMenuContent>
      <AddOrdersToRouteMenuContentList ref={rootRef}>
        {pendingRoutes.map((route) => (
          <AddOrdersToRouteMenuRouteCard
            key={`pending-route-${route.routeId}-vehicle${route.vehicleId}`}
            route={route}
            onClick={() => onClickCard(route)}
          />
        ))}

        {showLoading && <LoadingFallback size={50} />}

        {notActiveRoutes && (
          <Stack spacing={1}>
            <Typography
              variant="h6"
              textAlign="center"
              my="auto"
              fontWeight="bold"
            >
              No se encontraron rutas
            </Typography>

            <Box
              component="img"
              src={toAbsoluteUrl('/icons/icon_empty_routes.png')}
              alt="Empty Routes Icon"
              sx={{ height: 80, objectFit: 'contain' }}
            />
          </Stack>
        )}

        <div ref={sentryRef} />
      </AddOrdersToRouteMenuContentList>
    </AddOrdersToRouteMenuContent>
  );
};

export default AddOrdersToRouteMenuPending;
